<script lang="ts">
  import { onMount, untrack, type Snippet } from 'svelte';
  import type { AuthService } from '$lib/services/auth';
  import { AuthSession, type SignedIn } from '../auth-session.svelte';

  const { service, children }: { service: AuthService; children: Snippet<[SignedIn]> } = $props();

  // The service is fixed for the app's lifetime, so the session is made from its first value.
  const auth = untrack(() => new AuthSession(service));

  let email = $state('');
  let password = $state('');

  onMount(() => {
    void auth.start();
  });

  function submit(event: SubmitEvent, run: () => Promise<void>) {
    event.preventDefault();
    void run().then(() => (password = ''));
  }
</script>

{#if auth.view === 'signedIn' && auth.account}
  {@render children({
    email: auth.account.email,
    signOut: () => void auth.signOut(),
    renew: () => service.renew(),
    expired: () => void auth.expire(),
  })}
{:else}
  <main class="gate">
    <section class="card" aria-labelledby="auth-title">
      {#if auth.view === 'checking'}
        <p class="muted" role="status">Checking your sign-in…</p>
      {:else if auth.view === 'signIn'}
        <h1 id="auth-title">Sign in to Draw</h1>
        <form onsubmit={(event) => submit(event, () => auth.signIn(email, password))}>
          <label
            >Email <input type="email" autocomplete="email" required bind:value={email} /></label
          >
          <label
            >Password <input
              type="password"
              autocomplete="current-password"
              required
              bind:value={password}
            /></label
          >
          <button type="submit" class="primary" disabled={auth.busy}>Sign in</button>
        </form>
        <button type="button" class="link" onclick={() => auth.show('reset')}>
          Forgot your password?
        </button>
      {:else if auth.view === 'reset'}
        <h1 id="auth-title">Reset your password</h1>
        <form onsubmit={(event) => submit(event, () => auth.requestReset(email))}>
          <label
            >Email <input type="email" autocomplete="email" required bind:value={email} /></label
          >
          <button type="submit" class="primary" disabled={auth.busy}>Send reset link</button>
        </form>
        <button type="button" class="link" onclick={() => auth.show('signIn')}
          >Back to sign in</button
        >
      {:else if auth.view === 'invite' || auth.view === 'recovery'}
        <h1 id="auth-title">
          {auth.view === 'invite' ? 'Welcome to Draw' : 'Choose a new password'}
        </h1>
        <form
          onsubmit={(event) =>
            submit(event, () =>
              auth.view === 'invite' ? auth.acceptInvite(password) : auth.setPassword(password),
            )}
        >
          <label
            >New password <input
              type="password"
              autocomplete="new-password"
              minlength="8"
              required
              bind:value={password}
            /></label
          >
          <button type="submit" class="primary" disabled={auth.busy}>Set password</button>
        </form>
      {/if}

      {#if auth.notice}<p class="notice" role="status">{auth.notice}</p>{/if}
      {#if auth.error}<p class="error" role="alert">{auth.error}</p>{/if}
    </section>
  </main>
{/if}

<style>
  .gate {
    display: grid;
    place-items: center;
    height: 100vh;
    padding: var(--space-3);
  }

  .card {
    width: min(22rem, 100%);
    padding: var(--space-4);
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
  }

  h1 {
    margin: 0 0 var(--space-3);
    font-size: 1.25rem;
  }

  form {
    display: grid;
    gap: var(--space-3);
  }

  label {
    display: grid;
    gap: var(--space-1);
    font-size: 0.875rem;
    color: var(--color-text-muted);
  }

  input {
    padding: 8px 10px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
  }

  button {
    font: inherit;
    cursor: pointer;
  }

  .primary {
    padding: 8px 12px;
    border: 1px solid var(--color-accent);
    border-radius: 6px;
    background: var(--color-accent);
    color: #fff;
  }

  .primary:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .link {
    margin-top: var(--space-3);
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-accent);
    font-size: 0.875rem;
  }

  .muted,
  .notice {
    color: var(--color-text-muted);
    font-size: 0.875rem;
  }

  .error {
    color: var(--color-danger);
    font-size: 0.875rem;
  }
</style>
